/**
 * Display metadata — the vocabulary every screen shares.
 *
 * If two surfaces would otherwise disagree about what "Qualifying" is called,
 * what colour it is, or which operators its filter offers, the answer lives
 * here. Nothing in this file renders; it only describes.
 *
 * Colour is always expressed as a `var(--token)` reference so both themes
 * resolve automatically — never a hex.
 */

import {
  AlarmClock,
  Archive,
  Ban,
  Boxes,
  Building2,
  Calendar,
  CalendarClock,
  CircleCheck,
  CircleDollarSign,
  CircleDot,
  CircleSlash,
  CircleX,
  ClipboardList,
  Copy,
  Eye,
  Factory,
  FileText,
  Flame,
  Gauge,
  Globe,
  Handshake,
  Hash,
  Inbox,
  Layers,
  Link2,
  Mail,
  MapPin,
  MessageSquare,
  Paperclip,
  Route,
  Share2,
  ShieldCheck,
  Signal,
  SignalHigh,
  SignalLow,
  SignalMedium,
  Snowflake,
  Sparkles,
  Sprout,
  Tag as TagIcon,
  Timer,
  Trophy,
  Type,
  Upload,
  User as UserIcon,
  UserX,
  Users,
  Webhook,
  Wrench,
  Zap,
} from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

import { api } from '@/lib/api';
import {
  DEFAULT_STAGE_COLORS,
  INQUIRY_TYPES,
  REGIONS,
  SCORE_BANDS,
  SCORE_BAND_RANGES,
  SOURCES,
  STAGE_KEYS,
  STATUSES,
  TAG_COLORS,
  bandForScore,
  stageRampToken,
  type FilterOperator,
  type InquiryType,
  type Priority,
  type RangeKey,
  type Region,
  type ScoreBand,
  type SortSpec,
  type Source,
  type StageKey,
  type PipelineStage,
  type Status,
  type TagColor,
} from '@shared/types';

/**
 * Re-exported so a feature imports one module for its vocabulary. These are
 * *not* redeclared here — `@shared/types` owns the band cut-offs and the stage
 * colour rule, and the backend imports the same two symbols.
 */
export { bandForScore, stageRampToken };

/** Structural type for a lucide icon — the package does not export one. */
export type IconComponent = ComponentType<
  SVGProps<SVGSVGElement> & { size?: string | number; absoluteStrokeWidth?: boolean }
>;

/* ────────────────────────────────────────────────────────────────────────────
   Paging & layout defaults
   ──────────────────────────────────────────────────────────────────────────── */

/** One screen of rows plus a buffer, so the first scroll never hits a spinner. */
export const PAGE_SIZE = 50;
/** Cards fetched per board column before "show more". */
export const BOARD_PAGE_SIZE = 25;
/** Rows below which virtualisation is not worth its complexity. */
export const VIRTUALIZE_THRESHOLD = 60;

export const DEFAULT_SORT: SortSpec[] = [{ field: 'createdAt', direction: 'desc' }];

/**
 * The `/tasks` row track — THE one definition:
 *
 *   select · complete · inquiry ref · task · stage · slack · owner · due ·
 *   priority · actions
 *
 * There used to be two of these — this one and a second copy in
 * `features/tasks/taskTable.ts` — and they disagreed by 136px of fixed track and
 * 4px of gutter, so the route's Suspense fallback and the row it stood in for
 * were laid out on different grids and the whole table jumped sideways the
 * instant data landed. There is now one function, imported by the row, the
 * column head, the band and the skeleton alike.
 *
 * It lives here rather than in the feature because the route-level suspense
 * fallback in `components/app/RouteSkeletons` must not import a lazily-loaded
 * feature module. Change it here and all four move together.
 *
 * Why the tracks are the sizes they are:
 *
 *  - **select is 28px and complete is 24px, and they are two different tracks.**
 *    A square checkbox in the leading track means "select this row" on every
 *    other list in the product. Completion is a round toggle in its own slot, so
 *    one glyph never carries two verbs.
 *  - **inquiry ref is 72px** — the join key between a task and its inquiry, and
 *    a key you cannot run your eye down is not a key. The ref measures 49px at
 *    12px IBM Plex Mono and the `Inquiry` head 57px at 11px/`--tracking-label`,
 *    so 72px holds the wider of the two with 15px to spare. It was 96px, which
 *    held them with 39px to spare — reserved width doing nothing on every row,
 *    on the same grid as the three enum tracks below.
 *  - **the task track is capped at 760px, not `1fr`.** At 1920 an uncapped track
 *    measured 1092px against 470–620px of ink, i.e. ~28% of every row blank
 *    inside a cell that pretended to be full. The cap is a little over the
 *    widest measured title+subject cluster.
 *  - **there is no slack track, and that is deliberate.** There used to be a
 *    `minmax(0,1fr)` between Stage and Owner. At 1440 it resolved to 0 and the
 *    row was excellent; at 1920 it resolved to 256px and sat *inside* the
 *    record — 280px of empty ground between the Stage cell's right edge and the
 *    Owner cell's left edge, on every one of 50 rows, splitting each record into
 *    two clusters with Stage stranded on the far side of the gap from both. Now
 *    every track is a data track. The row's tracks are all fixed or capped, so a
 *    grid container that is wider than they need distributes the surplus at the
 *    inline-start edge's opposite end (`justify-content` resolves to `start` for
 *    non-`auto` tracks): the leftover becomes page margin OUTSIDE the record
 *    instead of a hole inside it, and the 1440 arrangement is what 1920 shows.
 *    `scripts/invariants-check.mjs` asserts no non-data track exceeds 48px at
 *    1920.
 *  - **owner is 152px** because owner renders as a 22px disc plus a name
 *    everywhere in the product.
 *  - **the three enum tracks are sized to their own ink, not to a round
 *    number.** Stage, Due and Priority hold strings whose longest form is known:
 *    `Proposal` at 49px, `20d Jul 17` at 61px and `Normal` at ~44px (with its
 *    14px glyph and 4px gap). They used to reserve 132px, 132px and 64px — 187px of
 *    dead width spread across three cells on every row of the queue, on the same
 *    screen that dropped the subject line entirely because the Task track came
 *    up short of its 540px threshold. So each is now its widest string plus the
 *    12px gutter, plus room for the head's own label and sort arrow where it has
 *    one — 88px, 104px and 72px — and what that buys goes to the Task track,
 *    which is where the reading is. Priority is 72px rather than 44px because
 *    the cell prints `Normal`, not `P3`: a field may not wear one vocabulary in
 *    its list and another in every control that sets it.
 */
export interface TaskColumnVisibility {
  inquiry: boolean;
  /** The parent inquiry's pipeline stage — what the task is actually against. */
  stage: boolean;
  owner: boolean;
  priority: boolean;
}

export const TASK_COLUMNS_ALL: TaskColumnVisibility = {
  inquiry: true,
  stage: true,
  owner: true,
  priority: true,
};

/**
 * Optional columns are dropped from the track list rather than collapsed to
 * `0px`: a zero-width track still eats its 12px gap, which would put the row's
 * fixed cluster out of register with the head's.
 *
 * THE SURPLUS BELONGS TO THE TITLE, NOT TO THE ROW'S RIGHT EDGE. The Task track
 * was `minmax(0,760px)`, which is exactly right at 1440 (it resolves below 760
 * there, so nothing about that layout moves) and wrong at 1920: the tracks then
 * summed to 1428 inside a 1664px content box, leaving 240px — 14.4% of the row —
 * of empty ground to the right of the last cell on every one of 49 rows, with
 * the hover wash and the hairline divider painting across it. `1fr` right-anchors
 * the metadata cluster the way Linear's list does and spends the surplus on the
 * one field that can always use more of it. The previous `row-geometry`
 * assertion could not see this: it measures declared tracks, not the grid's
 * residual free space, so it passed on a row that was 240px short of its edge.
 */
export function taskGrid(columns: TaskColumnVisibility = TASK_COLUMNS_ALL): string {
  return [
    '28px',
    '24px',
    columns.inquiry ? '72px' : null,
    'minmax(0,1fr)',
    columns.stage ? '88px' : null,
    columns.owner ? '152px' : null,
    '104px',
    columns.priority ? '72px' : null,
    '28px',
  ]
    .filter(Boolean)
    .join(' ');
}

/** How many tracks `taskGrid` emits — the row's `aria-colcount`. */
export function taskColumnCount(columns: TaskColumnVisibility = TASK_COLUMNS_ALL): number {
  return taskGrid(columns).split(' ').length;
}

export const TASK_GRID = taskGrid();

/** The band's own two tracks — chevron over the select column, label after it. */
export const TASK_BAND_GRID = '28px minmax(0,1fr)';

/** Sticky column head. */
export const TASK_HEAD_H = 28;
/** Sticky group band. */
export const TASK_BAND_H = 32;

export const DENSITY_OPTIONS = [
  { value: 'comfortable', label: 'Comfortable', rowHeight: 'var(--h-row)' },
  { value: 'compact', label: 'Compact', rowHeight: 'var(--h-row-compact)' },
] as const;
export type Density = (typeof DENSITY_OPTIONS)[number]['value'];

/** Task row pitch, from the shared density control. */
export const TASK_ROW_HEIGHT: Record<Density, string> = {
  comfortable: 'var(--h-row)',
  compact: 'var(--h-row-compact)',
};

/* ────────────────────────────────────────────────────────────────────────────
   Named measures
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Measures that used to be typed straight into a component as `w-[560px]` /
 * `max-h-[320px] w-[248px]`.
 *
 * The px values themselves now live in `styles/tokens.css` beside the other
 * fixed chrome (`--h-row`, `--w-detail`), because a measure a component is laid
 * out on has to be readable from a stylesheet, a media query and any consumer
 * that is not Tailwind. What is left here is the *name a component asks for it
 * by* — the class string — so there is still exactly one definition and one
 * import for a screen to reconcile against.
 */

/** Reading measure of an empty-state block sitting in a list pane. */
export const W_EMPTY_MEASURE = 560;

/**
 * A scrolling submenu of names: wide enough for a full name plus a radio mark,
 * capped at roughly ten rows before it scrolls. `--w-menu-md` / `--h-menu-scroll`.
 */
export const MENU_SCROLL_PANEL =
  'max-h-[var(--h-menu-scroll)] w-[var(--w-menu-md)] overflow-y-auto';

/**
 * The four control widths a form row or a filter panel actually uses, named
 * once. They were typed straight into the task composer and the task filter
 * panel seven times over as `w-[300px]`, `w-[168px]`, `w-[148px]` and
 * `w-[112px]` — the same defect `W_EMPTY_MEASURE` and `MENU_SCROLL_PANEL` were
 * lifted out of those same two files to fix.
 *
 *   sm  a two-to-four character value (a priority code)
 *   md  a date
 *   lg  a person's name or a short phrase
 *   xl  a reference plus a subject line
 *
 * The measures are `--w-control-sm…xl` in `styles/tokens.css`, beside the rest
 * of the fixed chrome; this map is only the name a component asks for one by.
 */
export const W_CONTROL = {
  sm: 'w-[var(--w-control-sm)]',
  md: 'w-[var(--w-control-md)]',
  lg: 'w-[var(--w-control-lg)]',
  xl: 'w-[var(--w-control-xl)]',
} as const;

/**
 * The task row's secondary string — the subject of the inquiry the task hangs
 * off — has a floor and a threshold, and below the threshold it does not render
 * at all.
 *
 * The Task track used to resolve to 420px at 1440. With the title taking its
 * natural 285–349px the subject was left 68–123px of a 192–323px string: 21–38%
 * of it, "Interior fini…", "Commerci…", which identifies nothing the AF-####
 * column has not already identified. So: the subject holds `min` or it is not
 * shown, and the title yields to it rather than the other way round — but only
 * once the cell is wide enough (`visible`) for both to be readable at the same
 * time. That is a *container* width, measured on the Task track itself, so it is
 * the same rule at every viewport and inside every column configuration.
 *
 * The threshold is arithmetic, not taste: 340px of title + the row's 12px gap +
 * the 168px subject floor = 520. 340 is where the measured titles sit (227–349,
 * median 310); the two or three longest lose a few pixels to an ellipsis, which
 * is the trade the subject is worth. It was 540 while the track resolved to
 * 420px at 1440 — a threshold no real column configuration could reach, so the
 * subject was simply never drawn. Sizing the three enum tracks and the ref track
 * to their own ink (see `taskGrid`) lifts the track to 536px at 1440, and the
 * subject is back on the screen it was written for.
 */
export const TASK_SUBJECT = {
  /** Floor, in px, under which the subject is not worth rendering at all. */
  min: 168,
  /** Marks the Task cell as the container the threshold below is measured on. */
  container: '@container',
  /** 520px of Task track — 340px of title, the 12px gap, and the subject floor. */
  visible: 'hidden @min-[520px]:block',
} as const;

export const LAYOUT_OPTIONS = [
  { value: 'table', label: 'Table', icon: Layers as IconComponent },
  { value: 'board', label: 'Board', icon: Route as IconComponent },
  { value: 'split', label: 'Split', icon: Eye as IconComponent },
] as const;

export const RANGE_OPTIONS: Array<{ value: RangeKey; label: string; short: string }> = [
  { value: '7d', label: 'Last 7 days', short: '7d' },
  { value: '30d', label: 'Last 30 days', short: '30d' },
  { value: '90d', label: 'Last 90 days', short: '90d' },
  { value: '12m', label: 'Last 12 months', short: '12m' },
  { value: 'ytd', label: 'Year to date', short: 'YTD' },
  { value: 'all', label: 'All time', short: 'All' },
];

/* ────────────────────────────────────────────────────────────────────────────
   Pipeline stages
   ──────────────────────────────────────────────────────────────────────────── */

export interface StageMeta {
  key: StageKey;
  /**
   * The stage's ONE name — the `label` on the row the server sent, or the
   * seeded default until `/pipeline/stages` lands.
   *
   * There is deliberately no `short` beside it. There used to be, and it was
   * how "Qualifying" became "Qualify" on the tasks queue and on the board's
   * off-screen rail while every other surface spelled it out — one field, two
   * spellings, and no user-visible rule for which they were about to get.
   * A surface that is short of room truncates in CSS, which degrades to the
   * same word rather than to a different one.
   */
  label: string;
  description: string;
  order: number;
  /** Palette name, matching `PipelineStage.color` on the wire. */
  color: TagColor;
  /** Foreground / dot colour. */
  accent: string;
  /** Tint for chip backgrounds. */
  soft: string;
  icon: IconComponent;
  isTerminal: boolean;
  isWon: boolean;
}

const soften = (token: string, amount = 14): string =>
  `color-mix(in oklab, ${token} ${amount}%, transparent)`;

/**
 * Everything about a stage *except* its colour. The colour is not written here
 * because it is not the dashboard's to decide: it is stored per stage in
 * `pipeline_stages`, arrives on the wire, and is resolved by the one rule in
 * `@shared/types`. Hard-coding it here is what made Settings/Pipeline a screen
 * that configured colours nothing consumed.
 */
const STAGE_BASE: Record<StageKey, Omit<StageMeta, 'color' | 'accent' | 'soft'>> = {
  new: {
    key: 'new',
    label: 'New',
    description: 'Landed but not yet looked at.',
    order: 0,
    icon: Inbox as IconComponent,
    isTerminal: false,
    isWon: false,
  },
  triage: {
    key: 'triage',
    label: 'Triage',
    description: 'Routed to an owner, waiting on first response.',
    order: 1,
    icon: Route as IconComponent,
    isTerminal: false,
    isWon: false,
  },
  qualifying: {
    key: 'qualifying',
    label: 'Qualifying',
    description: 'Scope, budget and timeline being established.',
    order: 2,
    icon: Gauge as IconComponent,
    isTerminal: false,
    isWon: false,
  },
  review: {
    key: 'review',
    label: 'Review',
    description: 'Engineering or mold review in progress.',
    order: 3,
    icon: Eye as IconComponent,
    isTerminal: false,
    isWon: false,
  },
  proposal: {
    key: 'proposal',
    label: 'Proposal',
    description: 'Pricing issued, awaiting a decision.',
    order: 4,
    icon: FileText as IconComponent,
    isTerminal: false,
    isWon: false,
  },
  won: {
    key: 'won',
    label: 'Won',
    description: 'Signed and handed to delivery.',
    order: 5,
    icon: Trophy as IconComponent,
    isTerminal: true,
    isWon: true,
  },
  lost: {
    key: 'lost',
    label: 'Lost',
    description: 'Closed without an award.',
    order: 6,
    icon: CircleSlash as IconComponent,
    isTerminal: true,
    isWon: false,
  },
};

/**
 * STAGE COLOUR IS THE STAGE'S POSITION. One rule, no second opinion, and it
 * does not live here — `stageRampToken` is in `@shared/types`, so the API that
 * resolves `PipelineStage.colorToken` and the dashboard that paints it cannot
 * hold different opinions about what colour a stage is.
 *
 * It used to be a stored swatch on the server (`pipeline_stages.color` through
 * a `STAGE_COLOR_TOKENS` map) and a position on the client — two rules that
 * agreed only for a pipeline nobody had reordered. `stage.colorToken` is now
 * the value both sides compute, and this module simply passes it through.
 *
 * `soften` is the same wash every chip in the product uses, so a stage cell, a
 * board column head and a settings swatch are the same colour by construction
 * rather than by coincidence.
 */
function paintStage(
  base: Omit<StageMeta, 'color' | 'accent' | 'soft'>,
  color: TagColor,
  token: string,
): StageMeta {
  const accent = `var(${token})`;
  return { ...base, color, accent, soft: soften(accent, base.isWon ? 16 : 14) };
}

/**
 * The seeded defaults — what a freshly installed pipeline looks like, and what
 * every accessor falls back to for the frames before `/pipeline/stages` lands.
 * Live data replaces it wholesale through `setLivePipeline`, below.
 */
export const STAGE_META_DEFAULTS: Record<StageKey, StageMeta> = Object.fromEntries(
  STAGE_KEYS.map((key) => [
    key,
    paintStage(
      STAGE_BASE[key],
      DEFAULT_STAGE_COLORS[key],
      stageRampToken(
        STAGE_BASE[key].order + 1,
        STAGE_BASE[key].isTerminal,
        STAGE_BASE[key].isWon,
      ),
    ),
  ]),
) as Record<StageKey, StageMeta>;

const FALLBACK_STAGE: StageMeta = {
  ...STAGE_META_DEFAULTS.new,
  key: 'new',
  label: 'Unknown',
  description: 'Stage not present in this workspace.',
  accent: 'var(--text-faint)',
  soft: soften('var(--text-faint)'),
};

/**
 * The API-driven form: the label and the order come from the stage row the
 * server sent, and the hue is the `colorToken` that row already carries.
 * Renaming or reordering a stage in Settings → Pipeline therefore reaches every
 * pixel in the product through one value.
 */
export function stageMetaFrom(stage: PipelineStage): StageMeta {
  const base = stage.key in STAGE_BASE ? STAGE_BASE[stage.key] : FALLBACK_STAGE;
  return {
    ...paintStage(
      base,
      stage.color,
      /* `colorToken` is required on the wire; the `??` is the guard against a
         hand-built stage in a test, and it recomputes the SAME rule. */
      stage.colorToken ?? stageRampToken(stage.position, stage.isTerminal, stage.isWon),
    ),
    label: stage.label,
    order: stage.position - 1,
    isTerminal: stage.isTerminal,
    isWon: stage.isWon,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
   The live pipeline — ONE registry

   Twelve surfaces paint a stage and only four of them had loaded the pipeline.
   The rest called `stageMeta(key)` and got the seeded table, so a stage renamed
   in Settings reached the board and the tasks queue but not the inbox filter
   chip, the command palette, the contact record or the company record — one
   field, two spellings, decided by which component you happened to be looking
   at.

   So the loaded pipeline is published here the moment `/pipeline/stages`
   resolves (see `useStages` in `lib/queries`), and `stageMeta` reads it. There
   is one table of stage records in the client and every surface reads it,
   whether or not that surface knows how to fetch a pipeline. The app shell
   mounts the query once, so the registry is warm before any screen renders.
   ──────────────────────────────────────────────────────────────────────────── */

let LIVE_STAGE_META: Record<StageKey, StageMeta> = STAGE_META_DEFAULTS;
let LIVE_STAGE_ORDER: StageKey[] = [...STAGE_KEYS];
let LIVE_STAGE_VERSION = 0;
const STAGE_LISTENERS = new Set<() => void>();

/**
 * Publish a loaded pipeline. Idempotent: re-publishing the same rows does not
 * bump the version, so a refetch that changed nothing repaints nothing.
 */
export function setLivePipeline(stages: readonly PipelineStage[] | undefined): void {
  if (stages === undefined || stages.length === 0) return;
  const next = { ...STAGE_META_DEFAULTS };
  const order: StageKey[] = [];
  for (const stage of [...stages].sort((a, b) => a.position - b.position)) {
    if (!(stage.key in STAGE_BASE)) continue;
    next[stage.key] = stageMetaFrom(stage);
    order.push(stage.key);
  }
  if (order.length === 0) return;
  const changed =
    order.join() !== LIVE_STAGE_ORDER.join() ||
    STAGE_KEYS.some(
      (key) =>
        next[key].label !== LIVE_STAGE_META[key].label ||
        next[key].accent !== LIVE_STAGE_META[key].accent,
    );
  LIVE_STAGE_META = next;
  LIVE_STAGE_ORDER = order;
  if (!changed) return;
  LIVE_STAGE_VERSION += 1;
  for (const listener of STAGE_LISTENERS) listener();
}

/** `useSyncExternalStore` pair, so a surface can repaint when the pipeline lands. */
export function subscribeLivePipeline(listener: () => void): () => void {
  STAGE_LISTENERS.add(listener);
  return () => STAGE_LISTENERS.delete(listener);
}

export function livePipelineVersion(): number {
  return LIVE_STAGE_VERSION;
}

/**
 * THE stage record, by key. Every surface in the product resolves a stage's
 * label and its colour through this function or through `stageMetaFrom` on a
 * row it was handed — there is no third way to find out what a stage is called
 * or what colour it is.
 */
export function stageMeta(key: string | null | undefined): StageMeta {
  if (key && key in LIVE_STAGE_META) return LIVE_STAGE_META[key as StageKey];
  return FALLBACK_STAGE;
}

/** The whole live table, in pipeline order. */
export function stageMetaAll(): StageMeta[] {
  return LIVE_STAGE_ORDER.map((key) => LIVE_STAGE_META[key]);
}

/** Is this string a stage key the workspace knows about? */
export function isStageKey(key: string): key is StageKey {
  return key in LIVE_STAGE_META;
}

/**
 * The canonical stage keys, in wire order. This is the *identity* order — what
 * "at or past qualifying" means — and it is fixed by `STAGE_KEYS`. For the
 * order a workspace actually renders in (which Settings → Pipeline can change),
 * use `stageMetaAll()`, which walks the loaded pipeline's own positions.
 */
export const STAGE_ORDER: StageKey[] = [...STAGE_KEYS];

/**
 * Index a loaded pipeline by key, for a component that already holds the query
 * result and wants to re-render when it changes. Identical output to
 * `stageMeta` — same records, same rule.
 */
export function stageMetaMap(
  stages: readonly PipelineStage[] | undefined,
): Record<StageKey, StageMeta> {
  if (stages === undefined || stages.length === 0) return LIVE_STAGE_META;
  const out = { ...STAGE_META_DEFAULTS };
  for (const stage of stages) {
    if (stage.key in STAGE_BASE) out[stage.key] = stageMetaFrom(stage);
  }
  return out;
}

/* ────────────────────────────────────────────────────────────────────────────
   Tag palette
   ──────────────────────────────────────────────────────────────────────────── */

export interface TagColorMeta {
  key: TagColor;
  label: string;
  accent: string;
  soft: string;
}

/**
 * The eight tag hues map 1:1 onto the categorical viz ramp, so a tag keeps its
 * identity in a chart legend and both themes resolve without a second palette.
 */
const TAG_COLOR_SLOTS: Record<TagColor, number> = {
  blue: 1,
  gold: 2,
  sky: 3,
  violet: 4,
  rose: 5,
  teal: 6,
  amber: 7,
  slate: 8,
};

/**
 * What each slot is *called*. The stored key is a wire value with a CHECK
 * constraint behind it and cannot be renamed, but the label is what a swatch
 * announces and what a picker prints, so it has to be the truth about the
 * pixel: `sky` paints `--viz-3`, which is teal, and `teal` paints `--viz-6`,
 * which is green. Deriving the label from the key spelled two of the eight
 * swatches wrong, including in their `aria-label`s.
 *
 * `scripts/contrast-check.mjs` gates the eight accents pairwise, so a slot can
 * never again share a colour with its neighbour under a different name.
 */
const TAG_COLOR_LABELS: Record<TagColor, string> = {
  blue: 'Blue',
  gold: 'Gold',
  sky: 'Teal',
  violet: 'Violet',
  rose: 'Rose',
  teal: 'Green',
  amber: 'Orange',
  slate: 'Slate',
};

export const TAG_COLOR_META: Record<TagColor, TagColorMeta> = TAG_COLORS.reduce(
  (acc, key) => {
    const token = `var(--viz-${TAG_COLOR_SLOTS[key]})`;
    acc[key] = {
      key,
      label: TAG_COLOR_LABELS[key],
      accent: token,
      soft: soften(token, 16),
    };
    return acc;
  },
  {} as Record<TagColor, TagColorMeta>,
);

export const TAG_COLOR_ORDER: TagColor[] = [...TAG_COLORS];

/** Safe accessor — server-side tag colours could drift ahead of this list. */
export function tagColorMeta(color: string | null | undefined): TagColorMeta {
  if (color && color in TAG_COLOR_META) return TAG_COLOR_META[color as TagColor];
  return TAG_COLOR_META.slate;
}

/* ────────────────────────────────────────────────────────────────────────────
   Score bands

   The ramp is ordinal, not categorical, and it deliberately contains no red.
   Red in this product means *danger* — an SLA breach, a failed write — and a
   high-scoring lead is the opposite of a problem, so `hot` wears the brand gold
   and the ramp cools from there: gold → blue → slate → mist. The `--band-*`
   tokens carry exactly those four values, so a band's name and its token agree.
   ──────────────────────────────────────────────────────────────────────────── */

export interface BandMeta {
  key: ScoreBand;
  label: string;
  description: string;
  /** Inclusive lower bound, exclusive upper — except `hot`, which tops out at 100. */
  min: number;
  max: number;
  accent: string;
  soft: string;
  icon: IconComponent;
  order: number;
}

/**
 * `min` / `max` are read from `SCORE_BAND_RANGES`, not typed in. They used to be
 * typed in — 80/60/35 against a server that banded at 75/55/35 — so a lead
 * scoring 78 showed a Hot chip in the list and read "WARM" on the record one
 * click later. There is now no number here to get wrong.
 */
export const BAND_META: Record<ScoreBand, BandMeta> = {
  hot: {
    key: 'hot',
    label: 'Hot',
    description: 'Funded, scoped and moving — work it today.',
    ...SCORE_BAND_RANGES.hot,
    accent: 'var(--band-hot)',
    soft: 'var(--band-hot-soft)',
    icon: Flame as IconComponent,
    order: 0,
  },
  warm: {
    key: 'warm',
    label: 'Warm',
    description: 'Real project, one or two unknowns left.',
    ...SCORE_BAND_RANGES.warm,
    accent: 'var(--band-warm)',
    soft: 'var(--band-warm-soft)',
    icon: Zap as IconComponent,
    order: 1,
  },
  early: {
    key: 'early',
    label: 'Early',
    description: 'Genuine interest, no timeline or budget yet.',
    ...SCORE_BAND_RANGES.early,
    accent: 'var(--band-early)',
    soft: 'var(--band-early-soft)',
    icon: Sprout as IconComponent,
    order: 2,
  },
  cold: {
    key: 'cold',
    label: 'Cold',
    description: 'Low signal — nurture or close.',
    ...SCORE_BAND_RANGES.cold,
    accent: 'var(--band-cold)',
    soft: 'var(--band-cold-soft)',
    icon: Snowflake as IconComponent,
    order: 3,
  },
};

export const BAND_ORDER: ScoreBand[] = [...SCORE_BANDS];

/* ────────────────────────────────────────────────────────────────────────────
   Priority
   ──────────────────────────────────────────────────────────────────────────── */

export interface PriorityMeta {
  key: Priority;
  label: string;
  short: string;
  icon: IconComponent;
  accent: string;
  soft: string;
  /** Descending urgency, so `sort((a,b) => a.rank - b.rank)` reads correctly. */
  rank: number;
  /**
   * ONE legend for the whole field: `Urgent · P1`.
   *
   * The word first, because the word is what the list cell and the composer
   * print and what a reader says out loud; the code after it, because saved
   * filters and the API speak in `P1…P4`. `/tasks` shipped three renderings of
   * these four values on one screen — the Filter stacked "Urgent" over "P1",
   * the bulk bar's menu read `P1 · Urgent`, and the composer's select read
   * "Normal" — so a reader who set priority from the bulk bar read the code
   * first and from the Filter read the word first, eight seconds apart.
   * Derived here so a fourth control cannot invent a fourth form.
   */
  legend: string;
  /**
   * The ramp for the WORD, which is text and therefore owes 4.5:1 — not 3:1.
   *
   * `accent` above is the GLYPH's ramp. A 14px signal bar is a meaningful
   * graphic, so its floor is 3:1 and its bottom step can be a `color-mix` down
   * to 3.86:1 dark / 3.68:1 light. The word beside it is 11px type: below 4.5:1
   * it is an automatic reject, and measured against both grounds the dimmed step
   * fails in both themes. So the word takes the text ramp and stops at
   * `--text-muted`, whose own step is measured 5.35:1 dark / 5.66:1 light on
   * `--surface-1` (5.61 / 5.22 on `--canvas`).
   *
   * That leaves three inks for four values, and the fourth step is carried by
   * WEIGHT rather than by a fourth colour, because there is no colour below
   * `--text-muted` that still clears 4.5:1 — `--text-faint` measures 5.80 dark,
   * i.e. it is a step UP there, and every mix that stays legible is within 0.5 of
   * muted. `dim` is that step: Low prints at 400 where the other three print at
   * 500. Ink and weight together, so no two priorities are typographically
   * identical — which is the defect this replaced, where all four words rendered
   * `rgb(180,199,220)` / 500 / 11px and the whole rank signal was the glyph.
   */
  ink: string;
  /** The bottom of the ramp: same ink as `normal`, one weight step lighter. */
  dim: boolean;
}

/** The one legend form, built once. */
function priorityLegend(label: string, short: string): string {
  return `${label} · ${short}`;
}

/**
 * One glyph family for all four steps — four states of the same signal bar, so
 * bar count carries urgency (5/4/3/2 bars) — on a ramp that only ever loses
 * ink weight as urgency falls.
 *
 * **The ramp is monochrome, deliberately.** It used to run
 * `--danger` → `--warning` → `--text-muted` → dimmed, and both saturated steps
 * collided with something else in the same 300px of row: P1's `#fb7185` was
 * byte-identical to the overdue due-date beside it, so one row said "urgent" and
 * "this date has passed" in the same red; P2's `#fbbf24` sat ~14° of hue from
 * `--accent` #d9a441, so at 14px "high priority" and "the keyboard is here"
 * were the same yellow. Colour now says nothing here at all: the bar count is
 * the rank, the `P1…P4` code is the value, and the ink step is the emphasis —
 * which leaves exactly two semantic hues on a task row, red for a passed date
 * and gold for the accent.
 *
 * Ink weight still has to fall with the bar count: `--text-primary` →
 * `--text-secondary` → `--text-muted` → a step below it (3.55:1 dark /
 * 3.69:1 light), over the 3:1 a meaningful glyph owes and under every step
 * above it in both themes.
 */
const dimmed = (token: string, amount: number): string =>
  `color-mix(in srgb, ${token} ${amount}%, var(--surface-1))`;
export const PRIORITY_META: Record<Priority, PriorityMeta> = {
  urgent: {
    key: 'urgent',
    label: 'Urgent',
    short: 'P1',
    legend: priorityLegend('Urgent', 'P1'),
    ink: 'var(--text-primary)',
    dim: false,
    icon: Signal as IconComponent,
    accent: 'var(--text-primary)',
    /* The wash follows the ink, not a semantic hue. `--danger-soft` here and
       `--warning-soft` below meant the ramp still carried a red and an amber
       for anything that reached for `soft`, which is how the gold and the red
       kept growing back onto a field that is deliberately monochrome. */
    soft: soften('var(--text-primary)', 10),
    rank: 0,
  },
  high: {
    key: 'high',
    label: 'High',
    short: 'P2',
    legend: priorityLegend('High', 'P2'),
    ink: 'var(--text-secondary)',
    dim: false,
    icon: SignalHigh as IconComponent,
    accent: 'var(--text-secondary)',
    soft: soften('var(--text-secondary)', 10),
    rank: 1,
  },
  normal: {
    key: 'normal',
    label: 'Normal',
    short: 'P3',
    legend: priorityLegend('Normal', 'P3'),
    ink: 'var(--text-muted)',
    dim: false,
    icon: SignalMedium as IconComponent,
    accent: 'var(--text-muted)',
    soft: soften('var(--text-muted)', 10),
    rank: 2,
  },
  low: {
    key: 'low',
    label: 'Low',
    short: 'P4',
    legend: priorityLegend('Low', 'P4'),
    ink: 'var(--text-muted)',
    dim: true,
    icon: SignalLow as IconComponent,
    accent: dimmed('var(--text-muted)', 80),
    soft: soften('var(--text-muted)', 8),
    rank: 3,
  },
};

export const PRIORITY_ORDER: Priority[] = ['urgent', 'high', 'normal', 'low'];

/* ────────────────────────────────────────────────────────────────────────────
   Source
   ──────────────────────────────────────────────────────────────────────────── */

export interface SourceMeta {
  key: Source;
  label: string;
  short: string;
  description: string;
  icon: IconComponent;
  accent: string;
}

/**
 * Ink, for the same reason `REGION_ACCENTS` is ink — see the note there. These
 * five carried `--viz-1 / 4 / 8 / 3 / 6`, i.e. tag slots "Blue", "Violet",
 * "Slate", "Teal" and "Green" under different names, on a glyph that sits four
 * rows above the record's tag chips. The `Type` row in that same panel was
 * already `text-muted`; the panel now agrees with itself.
 */
export const SOURCE_META: Record<Source, SourceMeta> = {
  form: {
    key: 'form',
    short: 'Form',
    label: 'Contact form',
    description: 'Submitted from a marketing-site form.',
    icon: ClipboardList as IconComponent,
    accent: 'var(--text-muted)',
  },
  assistant: {
    key: 'assistant',
    short: 'Assistant',
    label: 'Site assistant',
    description: 'Qualified by the on-site assistant conversation.',
    icon: Sparkles as IconComponent,
    accent: 'var(--text-muted)',
  },
  import: {
    key: 'import',
    short: 'Import',
    label: 'Import',
    description: 'Loaded from a spreadsheet or migration.',
    icon: Upload as IconComponent,
    accent: 'var(--text-muted)',
  },
  api: {
    key: 'api',
    short: 'API',
    label: 'API',
    description: 'Posted by an integration.',
    icon: Webhook as IconComponent,
    accent: 'var(--text-muted)',
  },
  referral: {
    key: 'referral',
    short: 'Referral',
    label: 'Referral',
    description: 'Introduced by a partner or existing client.',
    icon: Share2 as IconComponent,
    accent: 'var(--text-muted)',
  },
};

export const SOURCE_ORDER: Source[] = [...SOURCES];

/* ────────────────────────────────────────────────────────────────────────────
   Status
   ──────────────────────────────────────────────────────────────────────────── */

export interface StatusMeta {
  key: Status;
  label: string;
  icon: IconComponent;
  accent: string;
  soft: string;
  /** Which `Badge` variant a chip should use. */
  tone: 'neutral' | 'accent' | 'info' | 'success' | 'warning' | 'danger';
}

/**
 * ONE definition of what a status looks like — the tone AND the mark.
 *
 * `lost` used to be `--danger`, so the inbox printed a RED "Lost" badge in the
 * Status column while the Stage column in the same row printed a GREY "Lost"
 * dot, and the record's stage picker taught the grey. Red in this product means
 * destructive or failed — an SLA breach, a write that did not land — and losing
 * a deal is neither; it is the pipeline's neutral terminal, which is what
 * `--stage-lost` already says. Won keeps `--success` because `--stage-won`
 * resolves to that exact token, so the two vocabularies agree by construction.
 *
 * `StatusDot` and the inbox's status cell both read `tone` from here. They used
 * to hold a private `STATUS_TONE` map each, and the two disagreed about
 * `archived` — `neutral` (`--text-secondary`) in one, `muted` (`--text-muted`)
 * in the other — so the same status was two different greys depending on which
 * component drew it.
 */
export const STATUS_META: Record<Status, StatusMeta> = {
  open: {
    key: 'open',
    label: 'Open',
    icon: CircleDot as IconComponent,
    accent: 'var(--info)',
    soft: 'var(--info-soft)',
    tone: 'info',
  },
  won: {
    key: 'won',
    label: 'Won',
    icon: CircleCheck as IconComponent,
    accent: 'var(--stage-won)',
    soft: 'var(--success-soft)',
    tone: 'success',
  },
  lost: {
    key: 'lost',
    label: 'Lost',
    icon: CircleX as IconComponent,
    accent: 'var(--stage-lost)',
    soft: soften('var(--stage-lost)', 14),
    tone: 'neutral',
  },
  spam: {
    key: 'spam',
    label: 'Spam',
    icon: Ban as IconComponent,
    accent: 'var(--warning)',
    soft: 'var(--warning-soft)',
    tone: 'warning',
  },
  archived: {
    key: 'archived',
    label: 'Archived',
    icon: Archive as IconComponent,
    accent: 'var(--text-faint)',
    soft: soften('var(--text-faint)', 10),
    tone: 'neutral',
  },
};

export const STATUS_ORDER: Status[] = [...STATUSES];

/* ────────────────────────────────────────────────────────────────────────────
   Inquiry types
   ──────────────────────────────────────────────────────────────────────────── */

export type InquiryTypeGroup = 'Projects' | 'Plant & automation' | 'Partners' | 'General';

export interface InquiryTypeMeta {
  key: InquiryType;
  /** Verbatim form label. */
  label: string;
  /** Chip label — never wraps in a 140px column. */
  short: string;
  group: InquiryTypeGroup;
  icon: IconComponent;
}

const T = (
  key: InquiryType,
  short: string,
  group: InquiryTypeGroup,
  icon: IconComponent,
): InquiryTypeMeta => ({ key, label: key, short, group, icon });

export const INQUIRY_TYPE_META: Record<InquiryType, InquiryTypeMeta> = {
  'Submit Project Geometry': T('Submit Project Geometry', 'Geometry', 'Projects', Boxes),
  'Request Mold Review': T('Request Mold Review', 'Mold review', 'Projects', Wrench),
  'Request Project Review': T('Request Project Review', 'Project review', 'Projects', Eye),
  'Discuss a Commercial Project': T('Discuss a Commercial Project', 'Commercial', 'Projects', Building2),
  'Discuss an Industrial Application': T(
    'Discuss an Industrial Application',
    'Industrial',
    'Projects',
    Factory,
  ),
  'Discuss an Institutional Project': T(
    'Discuss an Institutional Project',
    'Institutional',
    'Projects',
    ShieldCheck,
  ),
  'General Contractor / CM Inquiry': T(
    'General Contractor / CM Inquiry',
    'GC / CM',
    'Projects',
    Users,
  ),
  'Modular Mold System Inquiry': T(
    'Modular Mold System Inquiry',
    'Modular molds',
    'Plant & automation',
    Layers,
  ),
  'Custom Interior Finishes': T('Custom Interior Finishes', 'Interiors', 'Projects', Sparkles),
  'Automation Retrofit Review': T(
    'Automation Retrofit Review',
    'Retrofit',
    'Plant & automation',
    Wrench,
  ),
  'Request New Plant Planning Review': T(
    'Request New Plant Planning Review',
    'New plant',
    'Plant & automation',
    Factory,
  ),
  'Enterprise Plant Standardization': T(
    'Enterprise Plant Standardization',
    'Standardization',
    'Plant & automation',
    Layers,
  ),
  'Apply for Partner Review': T('Apply for Partner Review', 'Partner apply', 'Partners', Handshake),
  'Partner Plant Review': T('Partner Plant Review', 'Partner plant', 'Partners', Handshake),
  'Enterprise / Multi-Region Inquiry': T(
    'Enterprise / Multi-Region Inquiry',
    'Enterprise',
    'Partners',
    Globe,
  ),
  'General Contact': T('General Contact', 'General', 'General', MessageSquare),
};

export const INQUIRY_TYPE_ORDER: InquiryType[] = [...INQUIRY_TYPES];

export const INQUIRY_TYPE_GROUPS: InquiryTypeGroup[] = [
  'Projects',
  'Plant & automation',
  'Partners',
  'General',
];

/** Safe accessor — the type list can grow server-side. */
export function inquiryTypeMeta(type: string | null | undefined): InquiryTypeMeta {
  if (type && type in INQUIRY_TYPE_META) return INQUIRY_TYPE_META[type as InquiryType];
  return {
    key: 'General Contact',
    label: type ?? 'Unknown',
    short: type ?? 'Unknown',
    group: 'General',
    icon: MessageSquare as IconComponent,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
   Regions & firmographics
   ──────────────────────────────────────────────────────────────────────────── */

export interface RegionMeta {
  key: Region;
  label: string;
  short: string;
  accent: string;
}

/**
 * NAMES, NOT HUES — the rule the board already applies to its owner, region and
 * source columns, now applied to the record and the filter builder too.
 *
 * These six used to be `--viz-1 / 8 / 3 / 4 / 5 / 7`, chosen to stay apart from
 * each other at a 6px dot. What they were not checked against is everything
 * ELSE that spends the same eight slots. `--viz-N` is the *categorical* ramp,
 * and the tag palette maps onto it 1:1 by design — `tagColorMeta('blue').accent`
 * IS `--viz-1` — so a "North America" dot and a tag chip named "Blue" rendered
 * the identical #8fc1f5, in the same 320px properties panel, meaning two
 * unrelated things. `--viz-1` was simultaneously chart-series-1, tag "Blue",
 * source "Contact form" and region "North America".
 *
 * A region is an identity, not an order and not a stored swatch: there is
 * nothing for a hue to encode that the word does not already say, which is
 * exactly the argument `columnIdentity` in `BoardScreen` makes for the same
 * dimension. So the mark is ink and the label does the work, and `--viz-*` is
 * left to mean one thing: a categorical slot in a chart, whose named face is
 * the tag palette.
 */
const REGION_ACCENTS: Record<Region, { short: string; accent: string }> = {
  'North America': { short: 'NA', accent: 'var(--text-muted)' },
  'Latin America': { short: 'LATAM', accent: 'var(--text-muted)' },
  Europe: { short: 'EU', accent: 'var(--text-muted)' },
  'Middle East': { short: 'ME', accent: 'var(--text-muted)' },
  'Asia Pacific': { short: 'APAC', accent: 'var(--text-muted)' },
  Africa: { short: 'AF', accent: 'var(--text-muted)' },
};

export const REGION_META: Record<Region, RegionMeta> = REGIONS.reduce(
  (acc, key) => {
    acc[key] = { key, label: key, ...REGION_ACCENTS[key] };
    return acc;
  },
  {} as Record<Region, RegionMeta>,
);

export const REGION_ORDER: Region[] = [...REGIONS];

/** Headcount buckets, matching `Company.size` on the wire. */
export const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-1000', '1000+'] as const;
export type CompanySize = (typeof COMPANY_SIZES)[number];

/**
 * The AEC segments the corpus actually carries — free text on the wire, so this
 * is a *seed* for a control, never the authority. `GET /companies/facets` is the
 * authority (`api.companies.facets`, `useCompanyFacets`), and anything that
 * narrows a list uses it, because a filter offering a value no row holds is a
 * bucket the reader can only find empty.
 *
 * This list had drifted three of eight values out of the 180-row book: it named
 * "Owner / Operator", "Public Agency" and "Industrial", which nothing carries,
 * and knew nothing about "Institution", "Industrial Owner" and "Modular
 * Builder", which 35 rows do. Counts at the time of writing: Precast Producer
 * 44 · Developer 41 · General Contractor 27 · Architecture 26 · Institution 18
 * · Industrial Owner 11 · Engineering 7 · Modular Builder 6.
 */
export const COMPANY_SEGMENTS = [
  'Architecture',
  'Developer',
  'Engineering',
  'General Contractor',
  'Industrial Owner',
  'Institution',
  'Modular Builder',
  'Precast Producer',
] as const;

/* ────────────────────────────────────────────────────────────────────────────
   Table columns
   ──────────────────────────────────────────────────────────────────────────── */

/** How a cell renders. The table maps these to renderers; the picker groups by them. */
export type CellKind =
  | 'ref'
  | 'subject'
  | 'text'
  | 'type'
  | 'contact'
  | 'company'
  | 'user'
  | 'stage'
  | 'status'
  | 'priority'
  | 'score'
  | 'sla'
  | 'tags'
  | 'source'
  | 'region'
  | 'date'
  | 'relative'
  | 'duration'
  | 'count'
  | 'number'
  | 'currency'
  | 'boolean';

export interface ColumnMeta {
  id: string;
  header: string;
  /** Dotted path into `Inquiry`. */
  accessor: string;
  /** Server field name for `sort` — defaults to `accessor` when omitted. */
  sortField?: string;
  width: number;
  minWidth: number;
  maxWidth?: number;
  align: 'left' | 'right' | 'center';
  sortable: boolean;
  /** Whether the header menu offers a filter (see `FILTER_FIELDS`). */
  filterable: boolean;
  /** Which `FILTER_FIELDS` entry the header menu opens. Required when filterable. */
  filterField?: string;
  defaultVisible: boolean;
  cell: CellKind;
  /** Grouping in the column picker. */
  group: 'Record' | 'People' | 'Pipeline' | 'Signals' | 'Dates' | 'Attribution';
  /** Tooltip in the column picker. */
  description?: string;
  /** Column stays put when the picker reorders. */
  pinned?: boolean;
}

export const INQUIRY_COLUMNS: ColumnMeta[] = [
  {
    id: 'ref',
    header: 'Ref',
    accessor: 'ref',
    width: 76,
    minWidth: 64,
    maxWidth: 110,
    align: 'left',
    sortable: true,
    filterable: true,
    filterField: 'ref',
    defaultVisible: true,
    cell: 'ref',
    group: 'Record',
    description: 'Human reference, e.g. AF-2418.',
    pinned: true,
  },
  {
    id: 'subject',
    header: 'Subject',
    accessor: 'subject',
    width: 268,
    minWidth: 180,
    align: 'left',
    sortable: true,
    filterable: true,
    filterField: 'subject',
    defaultVisible: true,
    cell: 'subject',
    group: 'Record',
    description: 'Subject line with the inquiry type beneath it.',
    pinned: true,
  },
  {
    id: 'type',
    header: 'Type',
    accessor: 'type',
    width: 148,
    minWidth: 96,
    align: 'left',
    sortable: true,
    filterable: true,
    filterField: 'type',
    defaultVisible: false,
    cell: 'type',
    group: 'Record',
    description: 'Which of the 16 form paths produced this.',
  },
  {
    id: 'company',
    header: 'Company',
    accessor: 'company.name',
    sortField: 'companyName',
    width: 150,
    minWidth: 110,
    align: 'left',
    sortable: true,
    filterable: true,
    filterField: 'companyId',
    defaultVisible: true,
    cell: 'company',
    group: 'People',
  },
  {
    id: 'contact',
    header: 'Contact',
    accessor: 'contact.fullName',
    sortField: 'contactName',
    width: 140,
    minWidth: 110,
    align: 'left',
    sortable: true,
    filterable: true,
    filterField: 'contactId',
    defaultVisible: true,
    cell: 'contact',
    group: 'People',
  },
  {
    id: 'owner',
    header: 'Owner',
    accessor: 'owner.name',
    sortField: 'ownerId',
    width: 52,
    minWidth: 44,
    maxWidth: 160,
    // Left, not centre. Centring an avatar+name cell hangs the 22px avatar off
    // the *middle* of the pair, so every row's avatar started at a different x
    // depending on the length of the owner's first name — measured across 13
    // rows the left edges spread 10.5px, and the dashed unassigned placeholder
    // sat 25px in from "Jae-won"'s. A column of avatars has to form a line.
    align: 'left',
    sortable: true,
    filterable: true,
    filterField: 'ownerId',
    defaultVisible: true,
    cell: 'user',
    group: 'People',
    description: 'Assigned rep. Widen the column to show names.',
  },
  {
    id: 'stage',
    header: 'Stage',
    accessor: 'stageKey',
    width: 116,
    minWidth: 92,
    align: 'left',
    sortable: true,
    filterable: true,
    filterField: 'stageKey',
    defaultVisible: true,
    cell: 'stage',
    group: 'Pipeline',
  },
  {
    id: 'status',
    header: 'Status',
    accessor: 'status',
    width: 100,
    minWidth: 84,
    align: 'left',
    sortable: true,
    filterable: true,
    filterField: 'status',
    defaultVisible: false,
    cell: 'status',
    group: 'Pipeline',
    description: 'Lifecycle state, orthogonal to pipeline stage.',
  },
  {
    id: 'priority',
    header: 'Priority',
    accessor: 'priority',
    width: 44,
    minWidth: 40,
    maxWidth: 120,
    // Left, for the same reason `owner` is: the glyph is the cell's first mark
    // and it belongs on the column's own vertical, not on the centre of a
    // glyph+word pair whose word changes length row to row.
    align: 'left',
    sortable: true,
    filterable: true,
    filterField: 'priority',
    defaultVisible: true,
    cell: 'priority',
    group: 'Pipeline',
  },
  {
    id: 'score',
    header: 'Score',
    accessor: 'score',
    width: 76,
    minWidth: 64,
    align: 'right',
    sortable: true,
    filterable: true,
    filterField: 'score',
    defaultVisible: true,
    cell: 'score',
    group: 'Signals',
    description: 'Server-computed 0–100 lead score with its band.',
  },
  {
    id: 'value',
    header: 'Value',
    accessor: 'value',
    width: 96,
    minWidth: 80,
    align: 'right',
    sortable: true,
    filterable: true,
    filterField: 'value',
    defaultVisible: false,
    cell: 'currency',
    group: 'Signals',
    description: 'Estimated contract value. Blank until somebody has scoped it.',
  },
  {
    id: 'sla',
    header: 'SLA',
    accessor: 'slaDueAt',
    width: 84,
    minWidth: 72,
    align: 'left',
    sortable: true,
    filterable: true,
    filterField: 'slaDueAt',
    defaultVisible: false,
    cell: 'sla',
    group: 'Signals',
    description: 'Time left to first response, or the breach.',
  },
  {
    id: 'tags',
    header: 'Tags',
    accessor: 'tags',
    sortField: 'tags',
    width: 132,
    minWidth: 90,
    align: 'left',
    sortable: false,
    filterable: true,
    filterField: 'tags',
    defaultVisible: true,
    cell: 'tags',
    group: 'Signals',
  },
  {
    id: 'source',
    header: 'Source',
    accessor: 'source',
    width: 104,
    minWidth: 84,
    align: 'left',
    sortable: true,
    filterable: true,
    filterField: 'source',
    defaultVisible: false,
    cell: 'source',
    group: 'Attribution',
  },
  {
    id: 'region',
    header: 'Region',
    accessor: 'region',
    width: 124,
    minWidth: 88,
    align: 'left',
    sortable: true,
    filterable: true,
    filterField: 'region',
    defaultVisible: false,
    cell: 'region',
    group: 'Attribution',
  },
  {
    id: 'createdAt',
    header: 'Created',
    accessor: 'createdAt',
    width: 88,
    minWidth: 76,
    align: 'right',
    sortable: true,
    filterable: true,
    filterField: 'createdAt',
    defaultVisible: true,
    cell: 'relative',
    group: 'Dates',
  },
  {
    id: 'lastActivityAt',
    header: 'Last activity',
    accessor: 'lastActivityAt',
    width: 100,
    minWidth: 80,
    align: 'right',
    sortable: true,
    filterable: true,
    filterField: 'lastActivityAt',
    defaultVisible: false,
    cell: 'relative',
    group: 'Dates',
  },
  {
    id: 'updatedAt',
    header: 'Updated',
    accessor: 'updatedAt',
    width: 92,
    minWidth: 76,
    align: 'right',
    sortable: true,
    filterable: true,
    filterField: 'updatedAt',
    defaultVisible: false,
    cell: 'relative',
    group: 'Dates',
  },
  {
    id: 'closedAt',
    header: 'Closed',
    accessor: 'closedAt',
    width: 92,
    minWidth: 76,
    align: 'right',
    sortable: true,
    filterable: true,
    filterField: 'closedAt',
    defaultVisible: false,
    cell: 'date',
    group: 'Dates',
  },
  {
    id: 'firstResponseMinutes',
    header: 'First response',
    accessor: 'firstResponseMinutes',
    width: 104,
    minWidth: 88,
    align: 'right',
    sortable: true,
    filterable: true,
    filterField: 'firstResponseMinutes',
    defaultVisible: false,
    cell: 'duration',
    group: 'Signals',
    description: 'Minutes from arrival to the first human reply.',
  },
  {
    id: 'notes',
    header: 'Notes',
    accessor: 'counts.notes',
    sortField: 'notesCount',
    width: 60,
    minWidth: 52,
    align: 'right',
    sortable: true,
    filterable: false,
    defaultVisible: false,
    cell: 'count',
    group: 'Record',
  },
  {
    id: 'openTasks',
    header: 'Open tasks',
    accessor: 'counts.openTasks',
    sortField: 'openTasksCount',
    width: 84,
    minWidth: 68,
    align: 'right',
    sortable: true,
    filterable: false,
    defaultVisible: false,
    cell: 'count',
    group: 'Record',
  },
  {
    id: 'attachments',
    header: 'Files',
    accessor: 'counts.attachments',
    sortField: 'attachmentsCount',
    width: 60,
    minWidth: 52,
    align: 'right',
    sortable: true,
    filterable: true,
    filterField: 'hasAttachments',
    defaultVisible: false,
    cell: 'count',
    group: 'Record',
  },
  {
    id: 'spamScore',
    header: 'Spam',
    accessor: 'spamScore',
    width: 64,
    minWidth: 56,
    align: 'right',
    sortable: true,
    filterable: true,
    filterField: 'spamScore',
    defaultVisible: false,
    cell: 'number',
    group: 'Signals',
    description: '0–100 heuristic; anything over 60 is quarantined.',
  },
  {
    id: 'utmSource',
    header: 'UTM source',
    accessor: 'utm.source',
    sortField: 'utmSource',
    width: 116,
    minWidth: 88,
    align: 'left',
    sortable: true,
    filterable: true,
    filterField: 'utmSource',
    defaultVisible: false,
    cell: 'text',
    group: 'Attribution',
  },
  {
    id: 'utmCampaign',
    header: 'UTM campaign',
    accessor: 'utm.campaign',
    sortField: 'utmCampaign',
    width: 140,
    minWidth: 96,
    align: 'left',
    sortable: true,
    filterable: true,
    filterField: 'utmCampaign',
    defaultVisible: false,
    cell: 'text',
    group: 'Attribution',
  },
];

export const INQUIRY_COLUMNS_BY_ID: Record<string, ColumnMeta> = Object.fromEntries(
  INQUIRY_COLUMNS.map((c) => [c.id, c]),
);

/** Ordered ids a fresh view starts with. */
export const DEFAULT_INQUIRY_COLUMNS: string[] = INQUIRY_COLUMNS.filter(
  (c) => c.defaultVisible,
).map((c) => c.id);

export const COLUMN_GROUP_ORDER: ColumnMeta['group'][] = [
  'Record',
  'People',
  'Pipeline',
  'Signals',
  'Dates',
  'Attribution',
];

/* ────────────────────────────────────────────────────────────────────────────
   Filter fields
   ──────────────────────────────────────────────────────────────────────────── */

export type FilterFieldType =
  | 'text'
  | 'select'
  | 'multiselect'
  | 'number'
  | 'date'
  | 'user'
  | 'tag'
  | 'boolean';

export interface FilterOption {
  value: string;
  label: string;
  /** Rendered as a leading dot/swatch. */
  color?: string;
  icon?: IconComponent;
  description?: string;
  group?: string;
}

export interface FilterFieldMeta {
  field: string;
  label: string;
  type: FilterFieldType;
  operators: FilterOperator[];
  icon: IconComponent;
  group: 'Pipeline' | 'People' | 'Signals' | 'Dates' | 'Content' | 'Attribution';
  /** Static option set for enum fields. */
  options?: FilterOption[];
  /**
   * Async option set for fields whose values live in the database. The filter
   * builder should route this through TanStack Query (`useUsers`, `useTags`, …)
   * rather than calling it directly on every keystroke.
   */
  loadOptions?: (signal?: AbortSignal) => Promise<FilterOption[]>;
  /** How `formatFilterValue` should render the value. */
  valueKind?: 'text' | 'number' | 'date' | 'minutes' | 'percent' | 'score' | 'currency';
  /** Bounds for numeric inputs. */
  min?: number;
  max?: number;
  unit?: string;
  description?: string;
}

const EQUALITY_OPS: FilterOperator[] = ['is', 'is_not'];
const SET_OPS: FilterOperator[] = ['in', 'not_in'];
const TEXT_OPS: FilterOperator[] = ['contains', 'not_contains', 'is', 'is_not', 'is_empty', 'is_not_empty'];
const NUMBER_OPS: FilterOperator[] = ['gt', 'gte', 'lt', 'lte', 'is', 'between'];
/**
 * A nullable number. Same arithmetic as `NUMBER_OPS`, plus the two operators
 * that ask whether anyone has filled it in at all — which is a real question
 * about `Inquiry.value` and the one the analytics coverage line links to.
 */
const VALUE_OPS: FilterOperator[] = [...NUMBER_OPS, 'is_empty', 'is_not_empty'];
/**
 * `gte`/`lte` are here because **the seeded views use them**, and their absence
 * was silently deleting the conditions that carried them: `sanitiseFilterSet`
 * drops a condition whose operator its field does not declare, so "Won this
 * quarter" rendered the chip `Closed ≥ Jun 30, 2026` and sent
 * `{status is won}` — a filter the UI showed and the API never applied, and a
 * toolbar reading "100 of 912" beside a sidebar badge of 10 for one view.
 * The backend's `DATE_OPS` (`lib/paging.ts`) has always accepted them.
 *
 * `is`/`is_not` stay out on purpose, in both directions of the contract: the
 * server accepts them, but an equality test against a millisecond timestamp is
 * a control that can only ever return nothing.
 */
const DATE_OPS: FilterOperator[] = ['gt', 'gte', 'lt', 'lte', 'between', 'is_empty', 'is_not_empty'];
const REL_OPS: FilterOperator[] = ['in', 'not_in', 'is_empty', 'is_not_empty'];

const enumOptions = <K extends string>(
  keys: readonly K[],
  meta: Record<K, { label: string; accent?: string; icon?: IconComponent; description?: string }>,
): FilterOption[] =>
  keys.map((key) => ({
    value: key,
    label: meta[key].label,
    color: meta[key].accent,
    icon: meta[key].icon,
    description: meta[key].description,
  }));

/**
 * The filter vocabulary — **the whole product's, not the inbox's**.
 *
 * Every name and every operator in here exists in the backend's `FIELD_MAP`
 * (`backend/src/lib/paging.ts`), and every field a seeded saved view filters on
 * is in here. That is not a nicety: a chip the UI can render but the API cannot
 * execute is a filter the user is told is on when it is not, and it is how the
 * product ended up printing "100 of 912 inquiries" for a view whose own sidebar
 * badge said 10.
 *
 * There used to be a "vocabulary bridge" in `features/inbox/FilterBuilder.tsx`
 * that patched the missing fields in at import time by mutating
 * `FILTER_FIELDS_BY_NAME`. It is gone. A table that is only correct once some
 * other module has been imported is not a table, and it could not have fixed
 * the real defect anyway — the missing entries were operators (`gte`, `lte`),
 * not fields.
 */
/**
 * Column ids the given sort is keyed on — see `@/lib/sortLock` for the rule.
 *
 * Matches on the server field name — `sortField ?? accessor` — which is the same
 * resolution `sortMenu` uses, so the menu and the lock can never disagree about
 * which column a sort belongs to.
 */
export function sortLockedColumnIds(
  sort: readonly SortSpec[],
  columns: readonly ColumnMeta[] = INQUIRY_COLUMNS,
): Set<string> {
  const fields = new Set(sort.map((entry) => entry.field));
  const locked = new Set<string>();
  for (const column of columns) {
    if (fields.has(column.sortField ?? column.accessor)) locked.add(column.id);
  }
  return locked;
}

export const FILTER_FIELDS: FilterFieldMeta[] = [
  {
    field: 'stageKey',
    label: 'Stage',
    type: 'multiselect',
    operators: [...SET_OPS, ...EQUALITY_OPS],
    icon: Layers as IconComponent,
    group: 'Pipeline',
    /* A getter, not a value: `FILTER_FIELDS` is built once at import and the
       pipeline lands later, so an eagerly-mapped array would freeze the seeded
       labels into the filter builder while every other surface showed the
       server's. */
    get options() {
      return stageMetaAll().map<FilterOption>((meta) => ({
        value: meta.key,
        label: meta.label,
        color: meta.accent,
        icon: meta.icon,
        description: meta.description,
      }));
    },
  },
  {
    /*
      "How far did it ever get", against `stageKey`'s "where is it now".

      The Stage-conversion funnel counts the first question — an inquiry that was
      qualified and then lost still reached Qualifying — and `stageKey` cannot
      express it, because an inquiry lost at Triage sits in `lost` exactly like
      one lost at Proposal. Every rung therefore deep-linked to a list 35–38%
      smaller than the number printed on it. The backend computes it from the
      same stage history the funnel's own `depth` CTE reads
      (`furthestStageKeySql` in `lib/paging.ts`), so the rung and its link now
      return the identical set.

      `lost` is not offered: losing is an exit, not progress, so it is excluded
      from the ladder on both sides and the column's domain is New … Won.
    */
    field: 'furthestStageKey',
    label: 'Furthest stage',
    type: 'multiselect',
    operators: [...SET_OPS, ...EQUALITY_OPS],
    icon: Layers as IconComponent,
    group: 'Pipeline',
    get options() {
      return stageMetaAll()
        .filter((meta) => meta.key !== 'lost')
        .map<FilterOption>((meta) => ({
          value: meta.key,
          label: meta.label,
          color: meta.accent,
          icon: meta.icon,
          description: `Ever reached ${meta.label}`,
        }));
    },
  },
  {
    field: 'status',
    label: 'Status',
    type: 'multiselect',
    operators: [...SET_OPS, ...EQUALITY_OPS],
    icon: CircleDot as IconComponent,
    group: 'Pipeline',
    options: enumOptions(STATUS_ORDER, STATUS_META),
  },
  {
    field: 'priority',
    label: 'Priority',
    type: 'multiselect',
    operators: [...SET_OPS, ...EQUALITY_OPS],
    icon: SignalHigh as IconComponent,
    group: 'Pipeline',
    options: enumOptions(PRIORITY_ORDER, PRIORITY_META),
  },
  {
    field: 'type',
    label: 'Inquiry type',
    type: 'multiselect',
    operators: [...SET_OPS, ...EQUALITY_OPS],
    icon: Boxes as IconComponent,
    group: 'Pipeline',
    options: INQUIRY_TYPE_ORDER.map((key) => ({
      value: key,
      label: INQUIRY_TYPE_META[key].short,
      description: key,
      group: INQUIRY_TYPE_META[key].group,
      icon: INQUIRY_TYPE_META[key].icon,
    })),
  },
  {
    field: 'ownerId',
    label: 'Owner',
    type: 'user',
    operators: REL_OPS,
    icon: UserIcon as IconComponent,
    group: 'People',
    loadOptions: async (signal) =>
      (await api.users.list(signal))
        .filter((u) => u.isActive)
        .map((u) => ({ value: u.id, label: u.name, description: u.title ?? u.email })),
  },
  {
    field: 'contactId',
    label: 'Contact',
    type: 'select',
    operators: REL_OPS,
    icon: UserIcon as IconComponent,
    group: 'People',
    loadOptions: async (signal) =>
      (await api.contacts.list({ limit: 50 }, signal)).items.map((c) => ({
        value: c.id,
        label: c.fullName,
        description: c.company?.name ?? c.email,
      })),
  },
  {
    field: 'companyId',
    label: 'Company',
    type: 'select',
    operators: REL_OPS,
    icon: Building2 as IconComponent,
    group: 'People',
    loadOptions: async (signal) =>
      (await api.companies.list({ limit: 50 }, signal)).items.map((c) => ({
        value: c.id,
        label: c.name,
        description: c.segment ?? c.domain ?? undefined,
      })),
  },
  {
    field: 'companySize',
    label: 'Company size',
    type: 'multiselect',
    operators: [...SET_OPS, ...EQUALITY_OPS, 'is_empty', 'is_not_empty'],
    icon: Layers as IconComponent,
    group: 'People',
    options: COMPANY_SIZES.map((size) => ({ value: size, label: `${size} people` })),
  },
  {
    field: 'companySegment',
    label: 'Company segment',
    type: 'multiselect',
    operators: [...SET_OPS, ...EQUALITY_OPS, 'is_empty', 'is_not_empty'],
    icon: Building2 as IconComponent,
    group: 'People',
    options: COMPANY_SEGMENTS.map((segment) => ({ value: segment, label: segment })),
  },
  {
    field: 'isUnassigned',
    label: 'Unassigned',
    type: 'boolean',
    operators: EQUALITY_OPS,
    icon: UserX as IconComponent,
    group: 'People',
    options: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
  },
  {
    // `tags`, not `tagIds` — the wire name in `FIELD_MAP`. Under the old name
    // every tag filter was a no-op the toolbar still counted.
    field: 'tags',
    label: 'Tags',
    type: 'tag',
    operators: REL_OPS,
    icon: TagIcon as IconComponent,
    group: 'Signals',
    loadOptions: async (signal) =>
      (await api.tags.list(signal)).map((t) => ({
        value: t.id,
        label: t.name,
        color: tagColorMeta(t.color).accent,
      })),
  },
  {
    field: 'score',
    label: 'Score',
    type: 'number',
    operators: NUMBER_OPS,
    icon: Gauge as IconComponent,
    group: 'Signals',
    valueKind: 'score',
    min: 0,
    max: 100,
    description: 'Server-computed lead score, 0–100.',
  },
  {
    field: 'value',
    label: 'Deal value',
    type: 'number',
    operators: VALUE_OPS,
    icon: CircleDollarSign as IconComponent,
    group: 'Signals',
    valueKind: 'currency',
    min: 0,
    unit: 'USD',
    /* `is_empty` is the point of this field being nullable: the analytics tile
       states how many open inquiries carry no value, and this is the control
       that opens exactly those rows. */
    description: 'Estimated contract value on the record. Empty until somebody scopes it.',
  },
  {
    field: 'scoreBand',
    label: 'Score band',
    type: 'multiselect',
    operators: [...SET_OPS, ...EQUALITY_OPS],
    icon: Flame as IconComponent,
    group: 'Signals',
    options: enumOptions(BAND_ORDER, BAND_META),
  },
  {
    field: 'firstResponseMinutes',
    label: 'First response',
    type: 'number',
    operators: [...NUMBER_OPS, 'is_empty', 'is_not_empty'],
    icon: Timer as IconComponent,
    group: 'Signals',
    valueKind: 'minutes',
    min: 0,
    unit: 'minutes',
    description: 'Minutes from arrival to first human reply.',
  },
  {
    field: 'slaDueAt',
    label: 'SLA due',
    type: 'date',
    operators: DATE_OPS,
    icon: CalendarClock as IconComponent,
    group: 'Signals',
    valueKind: 'date',
  },
  {
    field: 'spamScore',
    label: 'Spam score',
    type: 'number',
    operators: NUMBER_OPS,
    icon: ShieldCheck as IconComponent,
    group: 'Signals',
    valueKind: 'number',
    min: 0,
    max: 100,
  },
  {
    field: 'hasAttachments',
    label: 'Has attachments',
    type: 'boolean',
    operators: EQUALITY_OPS,
    icon: Paperclip as IconComponent,
    group: 'Signals',
    options: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
  },
  {
    // `isDuplicateOf` is a nullable id on the server, not a boolean, so the
    // question it can answer is "does it point at something".
    field: 'isDuplicateOf',
    label: 'Duplicate of',
    type: 'select',
    operators: ['is_empty', 'is_not_empty'],
    icon: Copy as IconComponent,
    group: 'Signals',
    description: 'Whether this submission was filed against an earlier one.',
  },
  {
    field: 'slaBreached',
    label: 'SLA breached',
    type: 'boolean',
    operators: EQUALITY_OPS,
    icon: AlarmClock as IconComponent,
    group: 'Signals',
    options: [
      { value: 'true', label: 'Yes' },
      { value: 'false', label: 'No' },
    ],
    description: 'Past the response target with no first reply.',
  },
  {
    field: 'source',
    label: 'Source',
    type: 'multiselect',
    operators: [...SET_OPS, ...EQUALITY_OPS],
    icon: Share2 as IconComponent,
    group: 'Attribution',
    options: enumOptions(SOURCE_ORDER, SOURCE_META),
  },
  {
    field: 'region',
    label: 'Region',
    type: 'multiselect',
    operators: [...SET_OPS, ...EQUALITY_OPS, 'is_empty', 'is_not_empty'],
    icon: MapPin as IconComponent,
    group: 'Attribution',
    options: REGION_ORDER.map((key) => ({
      value: key,
      label: key,
      color: REGION_META[key].accent,
    })),
  },
  {
    field: 'utmSource',
    label: 'UTM source',
    type: 'text',
    operators: TEXT_OPS,
    icon: Link2 as IconComponent,
    group: 'Attribution',
  },
  {
    field: 'utmCampaign',
    label: 'UTM campaign',
    type: 'text',
    operators: TEXT_OPS,
    icon: Link2 as IconComponent,
    group: 'Attribution',
  },
  {
    field: 'createdAt',
    label: 'Created',
    type: 'date',
    operators: DATE_OPS,
    icon: Calendar as IconComponent,
    group: 'Dates',
    valueKind: 'date',
  },
  {
    field: 'updatedAt',
    label: 'Updated',
    type: 'date',
    operators: DATE_OPS,
    icon: Calendar as IconComponent,
    group: 'Dates',
    valueKind: 'date',
  },
  {
    field: 'lastActivityAt',
    label: 'Last activity',
    type: 'date',
    operators: DATE_OPS,
    icon: Calendar as IconComponent,
    group: 'Dates',
    valueKind: 'date',
  },
  {
    field: 'closedAt',
    label: 'Closed',
    type: 'date',
    operators: DATE_OPS,
    icon: Calendar as IconComponent,
    group: 'Dates',
    valueKind: 'date',
  },
  {
    field: 'ref',
    label: 'Reference',
    type: 'text',
    operators: TEXT_OPS,
    icon: Hash as IconComponent,
    group: 'Content',
  },
  {
    field: 'subject',
    label: 'Subject',
    type: 'text',
    operators: TEXT_OPS,
    icon: Type as IconComponent,
    group: 'Content',
  },
  {
    field: 'message',
    label: 'Message',
    type: 'text',
    operators: TEXT_OPS,
    icon: MessageSquare as IconComponent,
    group: 'Content',
  },
  {
    field: 'contactName',
    label: 'Contact name',
    type: 'text',
    operators: TEXT_OPS,
    icon: UserIcon as IconComponent,
    group: 'Content',
  },
  {
    field: 'contactEmail',
    label: 'Contact email',
    type: 'text',
    operators: TEXT_OPS,
    icon: Mail as IconComponent,
    group: 'Content',
  },
  {
    // `contactPhone` used to sit here. The server has never had it, so every
    // condition built on it was dropped on the way to the wire.
    field: 'contactTitle',
    label: 'Contact title',
    type: 'text',
    operators: TEXT_OPS,
    icon: UserIcon as IconComponent,
    group: 'Content',
  },
  {
    field: 'companyName',
    label: 'Company name',
    type: 'text',
    operators: TEXT_OPS,
    icon: Building2 as IconComponent,
    group: 'Content',
  },
];

export const FILTER_FIELDS_BY_NAME: Record<string, FilterFieldMeta> = Object.fromEntries(
  FILTER_FIELDS.map((f) => [f.field, f]),
);

export const FILTER_GROUP_ORDER: FilterFieldMeta['group'][] = [
  'Pipeline',
  'People',
  'Signals',
  'Dates',
  'Content',
  'Attribution',
];

/** How each operator reads in a filter chip: "Stage **is** Qualifying". */
export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  is: 'is',
  is_not: 'is not',
  in: 'is any of',
  not_in: 'is none of',
  contains: 'contains',
  not_contains: 'does not contain',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  between: 'between',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
};

/** Operators that carry no value — the builder hides the value control. */
export const VALUELESS_OPERATORS: FilterOperator[] = ['is_empty', 'is_not_empty'];

/** Operators whose value is a list. */
export const MULTI_VALUE_OPERATORS: FilterOperator[] = ['in', 'not_in', 'between'];

/* ────────────────────────────────────────────────────────────────────────────
   Routes — one place the palette, sidebar and `g …` shortcuts agree on
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Account recovery.
 *
 * There is no self-serve reset endpoint and no `/forgot-password` route yet, so
 * recovery is a request to the people who *can* reset it. The affordance is
 * labelled for what it does — "Email support to reset", with a mail glyph — and
 * never as "Forgot password?", which promises a screen this build does not have.
 * When the reset flow lands, add `forgotPassword: '/forgot-password'` to
 * `ROUTES`, point the control at it and delete `PASSWORD_HELP_HREF`.
 */
export const SUPPORT_EMAIL = 'support@alphaform.com';

export const PASSWORD_HELP_HREF = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
  'AlphaForm CRM — password reset request',
)}`;

export const ROUTES = {
  inbox: '/inbox',
  board: '/board',
  contacts: '/contacts',
  companies: '/companies',
  tasks: '/tasks',
  analytics: '/analytics',
  settings: '/settings',
  /**
   * The onboarding, at an address. It is a route rather than a first-run modal
   * so it is still reachable in week three — see the header of
   * `features/onboarding/GettingStartedScreen`.
   */
  gettingStarted: '/getting-started',
  /**
   * Triage for the in-app reporter. Administrators only, and not under Settings
   * — it is a queue of work, like the Inbox, not a preference. Its rail row sits
   * in the footer group because it is a queue about the product rather than a
   * collection of customer records.
   */
  bugs: '/bugs',
  login: '/login',
  /**
   * Accepting a team invite. PUBLIC and parameterised by a one-time token, so
   * it sits outside `RequireAuth` next to the login screen — the person has no
   * session yet, and the token in the path is what stands in for one.
   */
  invite: '/invite/:token',
  resetPassword: '/reset-password',
  inquiry: (id: string) => `/inbox/${id}`,
  contact: (id: string) => `/contacts/${id}`,
  company: (id: string) => `/companies/${id}`,
} as const;
